import { Button, Card, Divider, Grid } from '@mantine/core';
import { httpsCallable } from 'firebase/functions';
import { notifications } from '@mantine/notifications';

import { BaseResult, CompanyRegistryBackfillRequest, CompanyRegistryBackfillResult, FundID } from '../../types';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { useContext, useState } from 'react';
import { GlobalDataContext } from '../../contexts';
import { doc, updateDoc } from 'firebase/firestore';
import { ordersPath } from '../../helpers/orderFilesPath';

const RefreshDataSettings = () => {
    const functions = useFunctions();
    const db = useFirestore();
    const globalDataContext = useContext(GlobalDataContext);


    const [loading, setLoading] = useState({
        kyc: false,
        expirey: false,
        customers: false,
        companies: false,
        napierRelated: false,
        orders: false,
        fundReports: false,
        campaigns: false,
        securities: false,
        transactions: false,
        rms: false,
        thumbnails: false,
        ordersPath: false,
        orderRefresh: false,

    })


    return (
        <Card shadow="sm" p="xl" radius="md" withBorder>


            <Grid gap={{ base: "xl", xs: "xl" }} >
                <Grid.Col span={4} >
                    <Button
                        loading={loading.kyc}
                        title='Manually refresh customers' onClick={async (e) => {
                            setLoading({ ...loading, kyc: true })
                            const currentTime = new Date();
                            const manualRefreshAllKYCFunction = httpsCallable<void, BaseResult>(functions, "manualRefreshAllKYC", { timeout: 1800000 });

                            //set type to submit because we want the user to create an entirely new KYC
                            await manualRefreshAllKYCFunction();
                            const timeAfterCalling = new Date();

                            // console the difference between the dates in seconds
                            const diffInSeconds = (timeAfterCalling.getTime() - currentTime.getTime()) / 1000;
                            console.log(`Time taken to refresh all customers: ${diffInSeconds} seconds`);
                            console.log("Finished refreshing customers KYCs");
                            setLoading({ ...loading, kyc: false })

                        }}>Manually refresh customers KYCs (please don't run this)</Button>

                </Grid.Col>
                <Grid.Col span={4} >
                    <Button
                        loading={loading.orderRefresh}
                        title='Manually refresh orders' onClick={async (e) => {
                            setLoading({ ...loading, orderRefresh: true })

                            const currentTime = new Date();
                            const manualRefreshAllOrdersFunction = httpsCallable<void, BaseResult>(functions, "manualRefreshAllOrders", { timeout: 1800000 });

                            //set type to submit because we want the user to create an entirely new order
                            await manualRefreshAllOrdersFunction();
                            const timeAfterCalling = new Date();

                            // console the difference between the dates in seconds
                            const diffInSeconds = (timeAfterCalling.getTime() - currentTime.getTime()) / 1000;
                            console.log(`Time taken to refresh all orders: ${diffInSeconds} seconds`);

                            console.log("Finished refreshing orders");
                            setLoading({ ...loading, orderRefresh: false })

                        }}>Manually refresh orders (please don't run this)</Button>

                </Grid.Col>

                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.expirey}
                        title='Manually refresh customers' onClick={async (e) => {
                            setLoading({ ...loading, expirey: true })

                            const expiredKYCSMSCallKYCFunction = httpsCallable<void, BaseResult>(functions, "expiredKYCSMSCall");

                            await expiredKYCSMSCallKYCFunction();
                            setLoading({ ...loading, expirey: false })

                        }}>Manually run KYC expiry sms (please don't run this)</Button>

                </Grid.Col>
                <Grid.Col span={4} >

                    <Button
                        loading={loading.customers}

                        title='Manually refresh customers'
                        onClick={async (e) => {
                            setLoading({ ...loading, customers: true })
                            const backfillCustomers = httpsCallable<void, BaseResult>(functions, "customersBackfill");

                            await backfillCustomers();
                            setLoading({ ...loading, customers: false })

                        }}>Manually backfill customers (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.companies}

                        title='Manually backfill company registry data'
                        onClick={async () => {
                            setLoading({ ...loading, companies: true })
                            const backfillCompanies = httpsCallable<CompanyRegistryBackfillRequest, CompanyRegistryBackfillResult>(functions, "companyRegistryBackfill", { timeout: 1800000 });

                            const result = await backfillCompanies({});
                            console.log("Company registry backfill result", result.data);
                            setLoading({ ...loading, companies: false })

                        }}>Manually backfill companies from MOCI</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.napierRelated}
                        title='Flags authorized signatories of active companies and relatives of active customers so Napier screens them. Safe to re-run.'
                        onClick={async () => {
                            setLoading({ ...loading, napierRelated: true })
                            try {
                                const refreshNapierRelated = httpsCallable<void, BaseResult>(functions, "manualRefreshNapierRelatedActiveStatus", { timeout: 1800000 });
                                const result = await refreshNapierRelated();
                                console.log("Napier related active status refresh result", result.data);
                                notifications.show({ color: "green", title: "Napier related active status refreshed", message: result.data.message ?? "Done", autoClose: false });
                            } catch (e: any) {
                                console.error("Napier related active status refresh failed", e);
                                notifications.show({ color: "red", title: "Napier related active status refresh failed", message: e?.message ?? "Unknown error", autoClose: false });
                            } finally {
                                setLoading({ ...loading, napierRelated: false })
                            }
                        }}>Refresh Napier related active status (signatories / relatives)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.orders}
                        title='Manually refresh orders' onClick={async (e) => {
                            setLoading({ ...loading, orders: true })

                            const backfillOrders = httpsCallable<void, BaseResult>(functions, "orderBackfill");

                            await backfillOrders();
                            setLoading({ ...loading, orders: false })


                        }}>Manually backfill orders (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.fundReports}
                        title='Manually backfill fund reports'
                        onClick={async () => {
                            setLoading({ ...loading, fundReports: true })

                            const backfillFundReports = httpsCallable<void, BaseResult>(functions, "fundReportsBackfill");

                            await backfillFundReports();
                            setLoading({ ...loading, fundReports: false })


                        }}>Manually backfill fund reports (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.campaigns}
                        title='Manually backfill campaigns'
                        onClick={async () => {
                            setLoading({ ...loading, campaigns: true })

                            const backfillCampaigns = httpsCallable<void, BaseResult>(functions, "campaignsBackfill");

                            await backfillCampaigns();
                            setLoading({ ...loading, campaigns: false })


                        }}>Manually backfill campaigns (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.securities}
                        title='Manually backfill securities'
                        onClick={async () => {
                            setLoading({ ...loading, securities: true })

                            const backfillSecurities = httpsCallable<void, BaseResult>(functions, "securitiesBackfill");

                            await backfillSecurities();
                            setLoading({ ...loading, securities: false })


                        }}>Manually backfill securities (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.transactions}

                        title='Manually refresh transactions' onClick={async (e) => {
                            const backfillTransactions = httpsCallable<void, BaseResult>(functions, "transactionBackfill");
                            setLoading({ ...loading, transactions: true })

                            await backfillTransactions();
                            setLoading({ ...loading, transactions: false })


                        }}>Manually backfill transactions (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.rms}
                        title='Manually refresh Rms' onClick={async (e) => {
                            setLoading({ ...loading, rms: true })
                            const backfillRms = httpsCallable<void, BaseResult>(functions, "RmsBackfill");

                            await backfillRms();
                            setLoading({ ...loading, rms: false })

                        }}>Manually backfill Rms (please don't run this)</Button>

                </Grid.Col>
                <Divider />
                <Grid.Col span={4} >

                    <Button
                        loading={loading.thumbnails}
                        title='Manually generate thumbnails' onClick={async (e) => {
                            setLoading({ ...loading, thumbnails: true })
                            const generateThumbnailstoFiles = httpsCallable<void, BaseResult>(functions, "generateThumbnailstoFiles");

                            await generateThumbnailstoFiles();
                            setLoading({ ...loading, thumbnails: false })


                        }}>Manually generate thumbnails (please don't run this)</Button>

                </Grid.Col>
                <Grid.Col span={4} >

                    <Button
                        loading={loading.ordersPath}
                        title='Copy OrdersPath from code to firestore' onClick={async (e) => {
                            setLoading({ ...loading, ordersPath: true })
                            const funds = globalDataContext.funds;

                            if (funds && funds.length > 0) {
                                for (let index = 0; index < funds.length; index++) {
                                    const fundID = funds[index].code;
                                    if (fundID) {
                                        const fundRef = doc(db, "funds", fundID);

                                        try {
                                            await updateDoc(fundRef, {
                                                "orders_path": {
                                                    "subscription": ordersPath["subscription"][fundID as FundID],
                                                    "redemption": ordersPath["redemption"][fundID as FundID],
                                                }
                                            });
                                        } catch (error) {
                                            console.log(`Error adding orders_path for ${fundID} and the error is: ${error}`,);
                                        }
                                    }



                                }

                            }
                            setLoading({ ...loading, ordersPath: false })


                        }}>Copy OrdersPath from code to firestore</Button>

                </Grid.Col>
            </Grid>

        </Card >
    )
}

export default RefreshDataSettings
